/** Pure helpers for the Track Statistics page. */

export interface PickStats {
  races: number;
  wins: number;
  places: number;
  roi: number | null;
}

export interface Bucket {
  key: string;
  races: number;
  favWins: number;
  favPlaces: number;
  tip: PickStats;
  consensus: PickStats;
  medianWinnerSp: number | null;
  winnersShort: number;
  winnersMid: number;
  winnersLong: number;
}

export interface BarrierCell {
  distanceBand: string;
  barrier: string;
  runners: number;
  wins: number;
  winIndex: number | null;
}

/** Below this many races (or runners) a figure is shown greyed out: too few to read anything into. */
export const MIN_SAMPLE = 20;

export const lowSample = (n: number): boolean => n < MIN_SAMPLE;

/** Strike rate as a percentage, or null when there is nothing to divide by. */
export const rate = (hits: number, of: number): number | null => (of > 0 ? (hits / of) * 100 : null);

export type Overlay = 'tip' | 'consensus';

/** One chart row: the favourite's strike and the chosen tips' strike, in the same bucket. */
export interface ChartRow {
  key: string;
  races: number;
  fav: number | null;
  tips: number | null;
  tipRaces: number;
  tipRoi: number | null;
  low: boolean;
}

export const chartRows = (buckets: Bucket[], overlay: Overlay): ChartRow[] =>
  buckets.map((b) => {
    const p = b[overlay];
    return {
      key: b.key,
      races: b.races,
      fav: rate(b.favWins, b.races),
      tips: rate(p.wins, p.races),
      tipRaces: p.races,
      tipRoi: p.roi,
      low: lowSample(b.races),
    };
  });

/** Heat-map tone for a barrier's win index (1.0 = fair share). */
export function indexTone(cell: Pick<BarrierCell, 'runners' | 'winIndex'> | undefined): 'low' | 'hot' | 'warm' | 'cool' | 'cold' | 'even' {
  if (!cell || cell.winIndex == null || lowSample(cell.runners)) return 'low';
  const i = cell.winIndex;
  if (i >= 1.3) return 'hot';
  if (i >= 1.1) return 'warm';
  if (i <= 0.7) return 'cold';
  if (i <= 0.9) return 'cool';
  return 'even';
}
